import React from 'react';
import { Box, ToggleButtonGroup, ToggleButton, Slider, IconButton, Tooltip } from '@mui/material';
import { Calendar, ZoomIn, ZoomOut, ArrowLeft, ArrowRight } from 'lucide-react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimelineView } from './utils/timelineUtils'; 

export interface TimelineControlsProps {
    view: TimelineView;
    onViewChange: (view: TimelineView) => void;
    zoomLevel: number;
    onZoomChange: (zoom: number) => void;
    startDate: Date;
    endDate: Date;
    onDateRangeChange: (start: Date, end: Date) => void;
  }

const TimelineControls: React.FC<TimelineControlsProps> = ({
  view,
  onViewChange,
  zoomLevel,
  onZoomChange,
  startDate,
  endDate,
  onDateRangeChange,
}) => {
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2,
        p: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper'
      }}>
        {/* View Toggle */}
        <ToggleButtonGroup
  value={view}
  exclusive
  onChange={(_, newView) => newView && onViewChange(newView)}
  size="small"
>
  <ToggleButton value="week">
    <Tooltip title="Weekly View">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Calendar size={16} />
        Week
      </Box>
    </Tooltip>
  </ToggleButton>
  <ToggleButton value="month">
    <Tooltip title="Monthly View">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Calendar size={16} />
        Month
      </Box>
    </Tooltip>
  </ToggleButton>
  <ToggleButton value="quarter">
    <Tooltip title="Quarterly View">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Calendar size={16} />
        Quarter
      </Box>
    </Tooltip>
  </ToggleButton>
  <ToggleButton value="year">
    <Tooltip title="Yearly View">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Calendar size={16} />
        Year
      </Box>
    </Tooltip>
  </ToggleButton>
</ToggleButtonGroup>

        {/* Zoom Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 200 }}>
          <IconButton 
            size="small"
            onClick={() => onZoomChange(Math.max(0.5, zoomLevel - 0.1))}
          >
            <ZoomOut size={16} />
          </IconButton>
          <Slider
            value={zoomLevel}
            min={0.5}
            max={2}
            step={0.1}
            onChange={(_, value) => onZoomChange(value as number)}
            sx={{ mx: 2 }}
          />
          <IconButton 
            size="small"
            onClick={() => onZoomChange(Math.min(2, zoomLevel + 0.1))}
          >
            <ZoomIn size={16} />
          </IconButton>
        </Box>

        {/* Date Range Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton 
            size="small"
            onClick={() => {
              const newStart = new Date(startDate);
              const newEnd = new Date(endDate);
              newStart.setMonth(newStart.getMonth() - 1);
              newEnd.setMonth(newEnd.getMonth() - 1);
              onDateRangeChange(newStart, newEnd);
            }}
          >
            <ArrowLeft size={16} />
          </IconButton>
          
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={(date) => date && onDateRangeChange(date, endDate)}
            slotProps={{ textField: { size: 'small' } }}
          />
          
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={(date) => date && onDateRangeChange(startDate, date)}
            slotProps={{ textField: { size: 'small' } }}
          />
          
          <IconButton 
            size="small"
            onClick={() => {
              const newStart = new Date(startDate);
              const newEnd = new Date(endDate);
              newStart.setMonth(newStart.getMonth() + 1);
              newEnd.setMonth(newEnd.getMonth() + 1);
              onDateRangeChange(newStart, newEnd);
            }}
          >
            <ArrowRight size={16} />
          </IconButton>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default TimelineControls;